/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, OnDestroy } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { AuthService } from 'src/app/service/auth.service';
import { YukkApi } from 'src/app/service/yukkapi.service';
import { FormControl } from '@angular/forms';
import {
  Observable,
  of,
  Subject,
  BehaviorSubject,
  combineLatest,
  throwError,
  forkJoin,
} from 'rxjs';
import {
  debounceTime,
  map,
  mergeMap,
  takeUntil,
  switchMap,
  tap,
  finalize,
  shareReplay,
  distinctUntilChanged,
  startWith,
  catchError,
} from 'rxjs/operators';
import * as moment from 'moment/moment';
import { UserAccount } from 'src/models/user.types';
import {
  ReportsService,
  ReportSubscription,
} from '../reports-setup/reports-service';
import { portfolioEvents } from './portfolio-events';

export interface PortfolioEntity {
  name: string;
  type: string;
  alpha_id: string;
}

@Injectable({
  providedIn: 'root',
})
export class PortfolioSetupService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private userAccountInfo: UserAccount | null = null;

  private portfolioNameSubject = new BehaviorSubject<string>('');
  portfolioName$ = this.portfolioNameSubject.asObservable();

  private selectedEntitiesSubject = new BehaviorSubject<PortfolioEntity[]>([]);
  selectedEntities$ = this.selectedEntitiesSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(false);
  loading$ = this.loadingSubject.asObservable();

  private setupProcessSubject = new BehaviorSubject<boolean>(false);
  setupProcess$ = this.setupProcessSubject.asObservable();

  searchFormControl = new FormControl('');
  items$: Observable<any>; // Results from the search API
  private _isSetUpComplete: boolean = false;

  constructor(
    private auth: AuthService,
    private yukkApi: YukkApi,
    private snackBar: MatSnackBar,
    private reportsService: ReportsService,
  ) {
    this.auth.accountInfo$
      .pipe(takeUntil(this.destroy$))
      .subscribe((accountInfo) => {
        this.userAccountInfo = accountInfo;
      });

    this.items$ = this.searchFormControl.valueChanges.pipe(
      startWith(''),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((value: string) => {
        if (value && value.length > 1) {
          this.loadingSubject.next(true);
          return this.yukkApi.search(value, false).pipe(
            mergeMap((result) => {
              if (result && result.hits.length === 0) {
                const words = value.split(' ').filter((item) => item !== '');
                return this.yukkApi
                  .search(words[0] ? words[0] : ' ', false)
                  .pipe(finalize(() => this.loadingSubject.next(false)));
              } else {
                return of(result).pipe(
                  finalize(() => this.loadingSubject.next(false)),
                );
              }
            }),
          );
        } else {
          this.loadingSubject.next(false);
          return of(null);
        }
      }),
      shareReplay(1),
      takeUntil(this.destroy$),
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.portfolioNameSubject.complete();
    this.selectedEntitiesSubject.complete();
    this.loadingSubject.complete();
  }

  setPortfolioName(name: string): void {
    this.portfolioNameSubject.next(name?.trim() || '');
  }

  addSelectedEntity(option: any): void {
    const currentEntities = this.selectedEntitiesSubject.getValue();
    const newEntity: PortfolioEntity = {
      name: option.entity.name,
      type: option.entity.type,
      alpha_id: option.entity.alpha_id,
    };
    if (!currentEntities.some((e) => e.alpha_id === newEntity.alpha_id)) {
      this.selectedEntitiesSubject.next([...currentEntities, newEntity]);
    }
    this.searchFormControl.setValue('');
  }

  removeEntity(idToRemove: string): void {
    const currentEntities = this.selectedEntitiesSubject.getValue();
    this.selectedEntitiesSubject.next(
      currentEntities.filter((e) => e.alpha_id !== idToRemove),
    );
  }

  clearSelection(): void {
    this.selectedEntitiesSubject.next([]);
    this.portfolioNameSubject.next('');
    this.searchFormControl.setValue('');
  }

  // --- Validation ---

  isValid$(): Observable<boolean> {
    return combineLatest([this.portfolioName$, this.selectedEntities$]).pipe(
      map(([name, entities]) => !!name && entities.length > 0),
      startWith(false),
    );
  }

  // --- Portfolio Creation Logic ---

  createPortfolio(): Observable<void> {
    this.setupProcessSubject.next(true);
    const name = this.portfolioNameSubject.getValue();
    const entities = this.selectedEntitiesSubject.getValue();

    if (!name || entities.length === 0) {
      this.snackBar.open('Portfolio name and entities are required.', 'OK', {
        duration: 3000,
      });
      return of(undefined);
    }
    const asyncOperations: Observable<any>[] = [];
    return this.auth.portFolios().pipe(
      switchMap((existingPortfolios) => {
        if (existingPortfolios && existingPortfolios.length > 0) {
          this.auth.deletePortfolio(existingPortfolios[0].uid).subscribe();
        }
        console.log('No existing portfolio to delete.');
        return of(undefined);
      }),

      switchMap(() => {
        return this.auth.newFolio(name).pipe(
          catchError((err) => {
            console.error('Error creating new folio:', err);
            this.snackBar.open('Failed to create new portfolio.', 'OK', {
              duration: 3000,
            });
            return throwError(() => new Error('New folio creation failed'));
          }),
        );
      }),

      switchMap((newPortfolioResult) => {
        if (!newPortfolioResult || !newPortfolioResult.id) {
          this.snackBar.open('Failed to get ID for new portfolio.', 'OK', {
            duration: 3000,
          });
          return throwError(() => new Error('Invalid new folio result'));
        }
        const entityIds = entities.map(
          (entity) => `${entity.type}:${entity.alpha_id}`,
        );

        return this.auth
          .addEntitiesToPortfolios(entityIds, [newPortfolioResult.id])
          .pipe(
            catchError((err) => {
              console.error('Error adding entities to portfolio:', err);
              this.snackBar.open('Failed to add entities to portfolio.', 'OK', {
                duration: 3000,
              });
              return throwError(() => new Error('Adding entities failed'));
            }),
            tap(() => {
              asyncOperations.push(
                this.generateNewsletter(newPortfolioResult.id, name),
                this.generateAlert(newPortfolioResult.id, name),
                this.generateReport(name, entityIds),
                this.createReportSubscription(newPortfolioResult.id),
              );
              forkJoin(asyncOperations)
                .pipe(
                  finalize(() => {
                    this.setSetUpComplete(true);
                    setTimeout(() => {
                      this.setupProcessSubject.next(false);
                    }, 3000);
                  }),
                )
                .subscribe({
                  next: () => {
                    this.snackBar.open(
                      'Portfolio created. Generating Newsletter, report, and alerts...',
                      'OK',
                      { duration: 3000 },
                    );
                  },
                  error: (error) => {
                    console.error('Error in portfolio setup:', error);
                    this.snackBar.open(
                      'Some portfolio features failed to setup.',
                      'OK',
                      {
                        duration: 1000,
                      },
                    );
                  },
                });
            }),
            catchError((error) => {
              console.error('Error in portfolio creation process:', error);
              this.snackBar.open('Failed to complete portfolio setup.', 'OK', {
                duration: 1000,
              });
              return throwError(() => error);
            }),
            map(() => undefined),
          );
      }),
      takeUntil(this.destroy$),
    );
  }

  // ... rest of the service

  defaultPortfolio(): Observable<void> {
    this.setupProcessSubject.next(true); // Start the setup process

    return this.auth.portFolios().pipe(
      tap((result) => {
        if (result && result.length > 0) {
          const defaultPortfolio = result[0];
          const asyncOperations = [
            this.generateNewsletter(
              defaultPortfolio.uid,
              defaultPortfolio.name,
            ),
            this.generateAlert(defaultPortfolio.uid, defaultPortfolio.name),
            this.generateReport(
              defaultPortfolio.name,
              defaultPortfolio.content,
            ),
            this.createReportSubscription(defaultPortfolio.uid),
          ];

          forkJoin(asyncOperations)
            .pipe(
              finalize(() => {
                this.setSetUpComplete(true);
                setTimeout(() => {
                  this.setupProcessSubject.next(false);
                }, 3000);
              }),
            )
            .subscribe({
              next: () => {
                this.snackBar.open(
                  'We are generating your default portfolio, preparing the weekly newsletter, the monthly report and the daily alert...',
                  'OK',
                  { duration: 3000 },
                );
              },
              error: (error) => {
                console.error('Error in portfolio setup:', error);
                this.snackBar.open(
                  'Some portfolio features failed to setup.',
                  'OK',
                  {
                    duration: 3000,
                  },
                );
              },
            });
        } else {
          this.setupProcessSubject.next(false);
          this.snackBar.open('No default portfolio found.', 'OK', {
            duration: 3000,
          });
        }
      }),
      catchError((error) => {
        console.error('Portfolio setup failed:', error);
        this.setupProcessSubject.next(false);
        this.snackBar.open('Portfolio setup failed.', 'OK', { duration: 3000 });
        return throwError(() => error);
      }),
      map(() => undefined),
      takeUntil(this.destroy$),
    );
  }

  setSetUpComplete(isSetupComplete: boolean): void {
    this._isSetUpComplete = isSetupComplete;
  }

  isSetupComplete(): boolean {
    return this._isSetUpComplete;
  }

  private generateNewsletter(id: string, name: string): Observable<any> {
    const newsletter = {
      active: true,
      contents: ['top_news'],
      delivery_settings: { frequency: 'WEEKLY' },
      dispObjects: [{ payload: id, type: 'portfolio', view: name }],
      displayables: ['ylrn:displayable:portfolio:' + id],
      frequency: 'WEEKLY',
      header_image:
        'https://yl-newsletters-logos-v1.s3.amazonaws.com/Logo_YUKKA_News-Analytics_Transparent.png',
      hide_sentiment_bars: false,
      logo: '',
      news_settings: { top_news_max_number: 20 },
      recipients: [],
      stories_max_number_default: 10,
      title: name + ' - Newsletter',
      top_news_max_number: 20,
      top_news_max_number_default: 7,
      uid: '',
    };
    return this.auth.editNewsletter(newsletter).pipe(
      switchMap((result2) => {
        if (result2 && result2.uid) {
          return this.auth.generateNewsletter({ uid: result2.uid });
        }
        return of(null);
      }),
      catchError((err) => {
        console.error('Error generating newsletter:', err);
        return of(null);
      }),
    );
  }

  private generateAlert(id: string, name: string): Observable<any> {
    const interestMapping = this.mapScoreTypeBasedOnInterest();
    if (!interestMapping) return;

    const payloadObject = {
      active: true,
      schedule: '1d',
      channel: 'email',
      layout: 'digest',
      name: 'Alert for ' + name,
      additional_recipients: [],
      event_alert_metadata: {
        portfolio_id: id,
        entities: [],
        event_ids: interestMapping.alertEvents,
        threshold: 5,
        threshold_setting: 'z_score',
        additional_params: {},
      },
    };
    return this.auth.addAlert(payloadObject).pipe(takeUntil(this.destroy$));
  }

  private generateReport(name: string, content: string[]): Observable<any> {
    const interestMapping = this.mapScoreTypeBasedOnInterest();
    if (!interestMapping) return;

    const type = interestMapping.reportType || interestMapping.score;
    const date = moment(new Date()).format('YYYY-MM-DD');
    return this.auth
      .sendReportPortfolio(name, content, type, date)
      .pipe(takeUntil(this.destroy$));
  }

  private createReportSubscription(portfolioId: string): Observable<any> {
    const interestMapping = this.mapScoreTypeBasedOnInterest();
    if (!interestMapping || !this.userAccountInfo) return;

    const scoreType = interestMapping.score;
    const subscription: ReportSubscription = {
      uid: undefined,
      displayable: 'ylrn:displayable:portfolio:' + portfolioId,
      active: true,
      schedule: 'every-month',
      score: scoreType,
      recipients: [],
    };
    return this.reportsService
      .createOrUpdateSubscription(subscription)
      .pipe(takeUntil(this.destroy$));
  }

  private mapScoreTypeBasedOnInterest():
    | { score: string; alertEvents: string[]; reportType?: string }
    | undefined {
    const interest = this.userAccountInfo?.interest;
    switch (interest) {
      case 'esg':
        return { score: 'esg', alertEvents: portfolioEvents.esgLab };
      case 'risk':
        return {
          score: 'general_risk',
          alertEvents: portfolioEvents.riskLab,
        };
      case 'news':
        return {
          score: 'general_risk',
          alertEvents: portfolioEvents.newsLab,
        };
      case 'invest':
        return {
          score: 'bb',
          reportType: 'investment',
          alertEvents: portfolioEvents.investLab,
        };
      default:
        return {
          score: 'general_risk',
          alertEvents: portfolioEvents.newsLab,
        };
    }
  }
}
